<template>
  <div>
    <div v-if="initialized">
      <!-- this is the 3 pane controller view  -->
      <div class="row mx-0 flex-nowrap" v-if="!selectIsMobileView">
        <!-- conversations list -->
        <div class="col-3 px-0" :style="paneMinWidths.conversation">
          <ConversationList
            @filtersUpdated="refreshLists('standardView')"
            @fetchMore="fetchMoreConversations('standardView')"
            :queryPending="queryPending"
            :fetchMorePending="fetchMorePending"
          />
        </div>

        <!-- messages window -->
        <div class="col px-0" :style="paneMinWidths.message">
          <MessageList @toggleDetails="toggleDetails" />
        </div>

        <!-- toggleable right pane -->
        <div
          class="col-3 px-0"
          v-if="selectShowDetailsPane && !selectIsMobileView"
          :style="paneMinWidths.details"
          :key="selectActiveConversation && selectActiveConversation._id"
        >
          <DetailsList />
        </div>
      </div>

      <!-- this is the mobile web app view, shown below MIN_DESKTOP_WIDTH -->
      <div v-else>
        <WinbackViewMobile
          :queryPending="queryPending"
          :fetchMorePending="fetchMorePending"
          @filtersUpdated="refreshLists('mobileView')"
          @fetchMore="fetchMoreConversations('mobileView')"
          @toggleDetails="toggleDetails"
        />
      </div>
    </div>

    <TimelineDrawer v-if="selectIsMobileView" ref="timelineDrawer" />
    <DetailsDrawer
      v-if="selectIsMobileView"
      ref="detailsDrawer"
      :key="selectActiveConversation && selectActiveConversation._id"
    />
  </div>
</template>

<script>
import ConversationList from './SubViews/ConversationList'
import TimelineDrawer from './Components/TimelineDrawer'
import DetailsDrawer from './Components/DetailsDrawer'
import MessageList from './SubViews/MessageList'
import DetailsList from './SubViews/DetailsList'
import WinbackViewMobile from './WinbackViewMobile'
import {
  CONVERSATION_PANE_MIN_WIDTH,
  MESSAGE_PANE_MIN_WIDTH,
  DETAILS_PANE_MIN_WIDTH,
  MIN_DESKTOP_WIDTH,
  DETAILS_PANE_BREAKPOINT
} from './paneWidths'
import { layoutWidth } from './layoutViewport'
import { trackEvent } from '@/lib/analytics'
import { createNamespacedHelpers } from 'vuex'
import { throttle } from 'lodash-es'

const LocationModule = createNamespacedHelpers('location')
const ConversationModuleV2 = createNamespacedHelpers('conversationV2')
const WebsocketModuleV2 = createNamespacedHelpers('websocketV2')
const CompanyModule = createNamespacedHelpers('company')
const GroupModule = createNamespacedHelpers('group')
const DataCoreModule = createNamespacedHelpers('dataCore')

const throttledFetchConversations = throttle(async (self, options) => {
  try {
    await self.fetchConversations(options)
  } catch (e) {
    if (self.selectFilters && self.selectFilters.search) {
      self.$notify({
        type: 'error',
        text: 'Sorry that search has too many results to process. Please try a different search.'
      })
    } else {
      self.$notify({
        type: 'error',
        text: 'Sorry something went wrong fetching the conversations.'
      })
    }
  }
}, 1000)

export default {
  name: 'WinbackView',
  components: {
    ConversationList,
    MessageList,
    DetailsList,
    WinbackViewMobile,
    TimelineDrawer,
    DetailsDrawer
  },
  data: () => ({
    initialized: false,
    queryPending: true,
    fetchMorePending: false
  }),
  async mounted() {
    // Initialize with base promises
    const mountPromises = [this.resetAllConversationData()]

    const { conversationId } = this.$route.params
    if (conversationId) {
      // Skip the timeline fetch here — locations aren't loaded yet, so it would
      // fetch with empty locationIds. parseRouteDetails() fetches it later.
      await this.fetchSpecificConversation({ conversationId, skipTimeline: true })
    }

    // Route through the centralized resync action — it handles ALL_COMPANIES vs
    // per-company and guards on connectionReady internally.
    if (this.selectConnectionReady) {
      mountPromises.push(this.resyncWinbackSubscription())
    }

    // Wait for initial setup
    await Promise.all(mountPromises)

    // Deep links may target a resolved conversation; drop the `resolved` filter
    // so it's fetched regardless of status. Awaited before the fetch below.
    if (conversationId) {
      await this.updateFilters({})
    }

    await Promise.all([
      this.getUserLocationsByActiveCompany(),
      throttledFetchConversations(this, { reset: true, trace: 'WinbackView: mounted' }),
      this.fetchGroups({
        memberType: 'location',
        companyIds: this.selectActiveCompanyIds
      })
    ])
    await this.checkSmallScreen()
    window.addEventListener('resize', this.checkSmallScreen)
    this.queryPending = false
    this.initialized = true
    await this.parseRouteDetails()
  },
  beforeDestroy() {
    this.closeActiveConversationChannel()
    this.winbackCompanyChannelUnsubscribe()
    this.resetAllConversationData()
    window.removeEventListener('resize', this.checkSmallScreen)
  },
  watch: {
    '$route.params.conversationId': {
      async handler(newId, oldId) {
        if (newId && newId !== oldId && this.initialized) {
          await this.parseRouteDetails()
        }
      }
    },
    selectConnectionReady: {
      async handler(val, oldVal) {
        if (val && !oldVal) {
          await this.resyncWinbackSubscription().catch((error) => console.log(error))
        }
      }
    },
    async 'activeCompany._id'(val, oldVal) {
      if (val !== oldVal) {
        await this.resyncWinbackSubscription().catch((error) => console.log(error))
      }
    }
  },
  computed: {
    ...DataCoreModule.mapGetters(['selectActiveCompanyIds']),
    ...CompanyModule.mapGetters(['activeCompany', 'selectCompany']),
    ...WebsocketModuleV2.mapGetters(['selectConnectionReady']),
    ...ConversationModuleV2.mapGetters([
      'selectIsMobileView',
      'selectIsMobileView',
      'selectShowDetailsPane',
      'selectActiveConversation',
      'selectFilters'
    ]),
    // Bound rather than written inline so the widths the row needs and the
    // width checkSmallScreen switches on cannot drift apart.
    paneMinWidths() {
      return {
        conversation: { minWidth: `${CONVERSATION_PANE_MIN_WIDTH}px` },
        message: { minWidth: `${MESSAGE_PANE_MIN_WIDTH}px` },
        details: { minWidth: `${DETAILS_PANE_MIN_WIDTH}px` }
      }
    }
  },
  methods: {
    ...LocationModule.mapActions(['getUserLocationsByActiveCompany']),
    ...ConversationModuleV2.mapActions([
      'fetchConversations',
      'resetAllConversationData',
      'setActiveConversation',
      'closeActiveConversationChannel',
      'updateIsMobileView',
      'fetchSpecificConversation',
      'updateFilters',
      'updateShowDetailsPane',
      'updateIsDeeplink'
    ]),
    ...WebsocketModuleV2.mapActions([
      'winbackCompanyChannelUnsubscribe',
      'resyncWinbackSubscription'
    ]),
    ...GroupModule.mapActions(['fetchGroups']),
    ...CompanyModule.mapActions(['setActiveCompany']),
    async checkSmallScreen() {
      const width = layoutWidth()

      if (width < MIN_DESKTOP_WIDTH && !this.selectIsMobileView) {
        this.showDetails = false
        this.updateIsMobileView(true)
      } else if (width >= MIN_DESKTOP_WIDTH && this.selectIsMobileView) {
        this.updateIsMobileView(false)
      }

      if (width > DETAILS_PANE_BREAKPOINT && !this.selectShowDetailsPane) {
        this.updateShowDetailsPane(true)
      } else if (width <= DETAILS_PANE_BREAKPOINT && this.selectShowDetailsPane) {
        this.updateShowDetailsPane(false)
      }
    },
    async parseRouteDetails() {
      // this function is only to account for deep linking
      const { conversationId } = this.$route.params
      try {
        if (conversationId) {
          await this.fetchSpecificConversation(conversationId)
          await this.updateIsDeeplink(true)
          trackEvent(this.$intercom, 'Deeplink')
          if (this.selectIsMobileView) {
            this.$nextTick(() => {
              this.$refs.timelineDrawer?.open()
            })
          }
        } else {
          // backward compatibility
          const { company: companyId, customer: customerId } = this.$route.query
          if (companyId && companyId !== this.activeCompany?._id) {
            const company = this.selectCompany(companyId)
            if (company) {
              await this.setActiveCompany(company)
            } else {
              throw new Error('No matching company for winback')
            }
          }
          if (customerId) {
            this.setActiveConversation({ customer: { _id: customerId } })
            trackEvent(this.$intercom, 'Deeplink')
            if (this.selectIsMobileView) {
              this.$refs.timelineDrawer.open()
            }
          }
        }
      } catch (error) {
        if (error?.status === 403) {
          this.$notify({
            type: 'error',
            text: "You don't have access to this conversation."
          })
          if (this.$route.params.conversationId) {
            this.$router.replace({ name: 'winback' })
          }
        } else {
          this.$notify({
            type: 'error',
            text: error?.message || "We couldn't load that conversation."
          })
        }
      }
    },
    toggleDetails() {
      this.updateShowDetailsPane(!this.selectShowDetailsPane)
      if (this.selectIsMobileView) {
        this.$refs.detailsDrawer.open()
      }
    },
    async refreshLists(view) {
      this.queryPending = true
      await throttledFetchConversations(this, {
        reset: true,
        trace: `WinbackView: refreshLists - ${view}`
      })
      this.queryPending = false
    },
    async fetchMoreConversations(view) {
      this.fetchMorePending = true
      await throttledFetchConversations(this, {
        reset: false,
        trace: `WinbackView: fetchMoreConversations - ${view}`
      })
      this.fetchMorePending = false
    }
  }
}
</script>

<style scoped>
.col {
  padding-left: 0px;
  padding-right: 0px;
}
</style>
